// Still-air / calm-sea range prediction by running the tick model to a dry tank (dtS = 60).
import type { CruiseControls } from '../types';
import type { ResolvedCraft } from './resolve';
import { isJet, sanitizeControls } from './resolve';
import { initialState as airInitial, stepAircraft, optimumAltitudeFt, STILL_AIR } from './aircraft';
import { initialState as seaInitial, stepVessel, CALM_SEA } from './vessel';

export interface RangePrediction {
  rangeNm: number;
  /** nm per kg (air) or nm per tonne (sea). */
  efficiency: number;
  hours: number;
}

export interface PredictOptions {
  /** Jets follow optimum-altitude step climbs (airline FMS practice) by default; pistons fly the planned altitude. */
  stepClimb?: boolean;
  dtS?: number;
}

const MAX_STEPS = 200 * 24 * 60; // 200 days at one-minute steps
const STEP_CLIMB_CHECK_S = 600;

export function predictRange(rc: ResolvedCraft, controls: CruiseControls, opts: PredictOptions = {}): RangePrediction {
  const dtS = opts.dtS ?? 60;
  const c = sanitizeControls(rc, controls);
  if (rc.sea) {
    let s = seaInitial(rc, c);
    for (let i = 0; i < MAX_STEPS && s.phase !== 'ended'; i++) s = stepVessel(rc, s, c, CALM_SEA, dtS);
    const used = rc.sea.fuelCapacityKg - s.fuelKg;
    return { rangeNm: s.distanceNm, efficiency: used > 0 ? (s.distanceNm * 1000) / used : 0, hours: s.timeS / 3600 };
  }
  const stepClimb = opts.stepClimb ?? isJet(rc);
  const plan = { ...c };
  let s = airInitial(rc, plan);
  let nextCheck = STEP_CLIMB_CHECK_S;
  for (let i = 0; i < MAX_STEPS && s.phase !== 'ended'; i++) {
    s = stepAircraft(rc, s, plan, STILL_AIR, dtS);
    if (stepClimb && s.phase === 'cruise' && s.timeS >= nextCheck) {
      nextCheck = s.timeS + STEP_CLIMB_CHECK_S;
      const opt = optimumAltitudeFt(rc, s, plan);
      if (opt >= s.altitudeFt + 2000) { plan.altitudeFt = opt; s = { ...s, phase: 'climb' }; }
    }
  }
  const capacity = rc.air ? rc.air.fuelCapacityKg : 0;
  const used = capacity - s.fuelKg;
  return { rangeNm: s.distanceNm, efficiency: used > 0 ? s.distanceNm / used : 0, hours: s.timeS / 3600 };
}
